import * as React from "react";
import { Dialog as SheetPrimitive } from "radix-ui";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "@/components/icons";

import { cn } from "@/lib/utils";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

function SheetOverlay({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      className={cn(
        "fixed inset-0 z-50 bg-black/35 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
      ref={ref}
    />
  );
}

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-surface p-6 shadow-modal data-[state=open]:ease-out data-[state=closed]:ease-in data-[state=closed]:duration-base data-[state=open]:duration-slow data-[state=open]:animate-in data-[state=closed]:animate-out",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 flex max-h-[calc(100dvh-env(safe-area-inset-top)-0.5rem)] flex-col rounded-t-2xl border-t pb-[max(1.5rem,env(safe-area-inset-bottom))] data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-full border-l sm:w-3/4 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
        none: "inset-0 h-full w-full data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

interface SheetContentProps
  extends
    React.ComponentProps<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  showCloseButton?: boolean;
  size?: "sm" | "md" | "lg" | "xl" | "full" | "fullscreen";
  layout?: "default" | "panel" | "grouped";
  overlayClassName?: string;
}

const sheetSizeClasses = {
  sm: "sm:max-w-md",
  md: "sm:max-w-xl",
  lg: "sm:max-w-2xl",
  xl: "sm:max-w-4xl",
  full: "sm:max-w-[calc(100vw-4rem)]",
  fullscreen: "inset-0 h-full w-full max-w-none sm:max-w-none border-0",
};

function SheetGrabber({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      data-slot="sheet-grabber"
      className={cn(
        "pointer-events-none absolute left-1/2 top-1.5 h-1 w-9 -translate-x-1/2 rounded-full bg-border-strong",
        className,
      )}
    />
  );
}

const sheetLayoutClasses = {
  default: undefined,
  panel: "flex flex-col gap-0 p-0",
  grouped: "gap-3 bg-surface-2 px-4 pt-6",
};

function layoutClassesFor(
  layout: NonNullable<SheetContentProps["layout"]>,
  side: SheetContentProps["side"],
) {
  return cn(
    sheetLayoutClasses[layout],
    layout === "panel" &&
      side === "bottom" &&
      "pt-4 pb-[env(safe-area-inset-bottom)]",
  );
}

const sideForSize = (
  size: SheetContentProps["size"],
  side: SheetContentProps["side"],
) => (size === "fullscreen" ? "none" : side);

function SheetContent({
  ref,
  side = "right",
  size,
  layout = "default",
  className,
  overlayClassName,
  children,
  showCloseButton = true,
  ...props
}: SheetContentProps) {
  return (
    <SheetPortal>
      <SheetOverlay
        className={cn(
          size === "fullscreen" && "bg-background",
          overlayClassName,
        )}
      />
      <SheetPrimitive.Content
        ref={ref}
        aria-describedby={props["aria-describedby"] ?? undefined}
        data-layout={layout}
        className={cn(
          "group/sheet",
          sheetVariants({ side: sideForSize(size, side) }),
          size && sheetSizeClasses[size],
          layoutClassesFor(layout, sideForSize(size, side)),
          className,
        )}
        {...props}
      >
        {sideForSize(size, side) === "bottom" ? <SheetGrabber /> : null}
        {showCloseButton && (
          <SheetPrimitive.Close className="absolute right-3 top-3 flex size-7 items-center justify-center rounded-md text-fg-subtle ring-offset-background transition-colors hover:bg-surface-2 hover:text-foreground focus:outline-none focus:ring-[3px] focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      "group-data-[layout=panel]/sheet:border-b group-data-[layout=panel]/sheet:px-4 group-data-[layout=panel]/sheet:py-3 group-data-[layout=panel]/sheet:pr-12 group-data-[layout=panel]/sheet:text-left",
      "group-data-[layout=grouped]/sheet:min-h-11 group-data-[layout=grouped]/sheet:flex-row group-data-[layout=grouped]/sheet:items-center group-data-[layout=grouped]/sheet:justify-between group-data-[layout=grouped]/sheet:space-y-0 group-data-[layout=grouped]/sheet:pl-4 group-data-[layout=grouped]/sheet:text-left",
      className,
    )}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      "group-data-[layout=panel]/sheet:gap-2 group-data-[layout=panel]/sheet:border-t group-data-[layout=panel]/sheet:px-4 group-data-[layout=panel]/sheet:py-3 group-data-[layout=panel]/sheet:sm:space-x-0",
      className,
    )}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

function SheetBody({
  ref,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }) {
  return (
    <div
      ref={ref}
      className={cn(
        "min-h-0 flex-1 overflow-y-auto",
        "group-data-[layout=panel]/sheet:flex group-data-[layout=panel]/sheet:flex-col group-data-[layout=panel]/sheet:gap-4 group-data-[layout=panel]/sheet:p-4",
        "group-data-[layout=grouped]/sheet:-mx-4 group-data-[layout=grouped]/sheet:px-4",
        className,
      )}
      {...props}
    />
  );
}

function SheetTitle({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      ref={ref}
      className={cn(
        "text-h3 font-semibold text-foreground",
        "group-data-[layout=panel]/sheet:text-body group-data-[layout=grouped]/sheet:text-title",
        className,
      )}
      {...props}
    />
  );
}

function SheetDescription({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      ref={ref}
      className={cn(
        "text-body text-muted-foreground",
        "group-data-[layout=panel]/sheet:text-caption",
        className,
      )}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetTitle,
  SheetDescription,
  SheetGrabber,
};
